"use client";

import Link from "@components/Link";
import { Button } from "@components/ngg";
import { useMarket } from "@context/marketContext";
import {
  ArrowEndIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  ArrowStartIcon,
} from "@ngg/icons";
import type { Filter } from "@server/utils/parseLoop/types";
import type { ElementType } from "react";
import { useMemo } from "react";
import type { Dictionary, Sort } from "types/common";

import { cn } from "@/lib/utils";
import { generateHash } from "@/utils/productSearch";

type PaginationProps = {
  page: number;
  slug: string;
  query?: string;
  state?: { sort?: Sort; filters: Filter[] };
  totalPages?: number;
  /**
   * Pages to show on each side of the current page, if 0 show all pages
   */
  pageRange?: number;
  ButtonComponent?: ElementType;
  PaginationComponent?: ElementType;
  classNames?: {
    pagesContainer?: string;
    pageButton?: string;
    endButtons?: string;
    stepButtons?: string;
    ellipsis?: string;
    innerContainer?: string;
  };
  dictionary: Dictionary;
};

const Pagination = ({
  page,
  slug,
  query,
  state,
  pageRange = 3,
  totalPages = 0,
  ButtonComponent = Button,
  PaginationComponent = "div",
  classNames,
  dictionary,
}: PaginationProps) => {
  const { locale } = useMarket().state.market;
  const pages = useMemo(() => {
    // Show all pages if pageRange is 0
    if (pageRange === 0) return Array.from({ length: totalPages }, (_, i) => i);
    const start = Math.max(0, page - pageRange);
    const end = Math.min(totalPages, page + pageRange + 1);

    // Create array from start to end
    return Array.from({ length: end - start }, (_, i) => i + start + 1);
  }, [page, totalPages, pageRange]);

  const currentHash = useMemo(() => {
    const [, hash] = generateHash(state?.sort, state?.filters).split("#");
    const baseurl = query ? `?query=${query}` : "";

    if (hash !== undefined) return `${baseurl}#${hash}`;

    return baseurl;
  }, [state?.sort, state?.filters, query]);

  const backButtonLink = useMemo(() => {
    const pageNumber = page <= 2 ? "" : `/${page - 1}${currentHash}`;
    return `/${slug}${pageNumber}${currentHash}`;
  }, [currentHash, page, slug]);

  if (totalPages <= 1) return null;
  return (
    <nav aria-label="Pagination" className="mt-1 border-t border-grey-150">
      <PaginationComponent
        className={cn(
          "flex h-14 items-center gap-2 px-3 lg:gap-3",
          classNames?.innerContainer,
        )}
      >
        <ButtonComponent
          as={Link}
          locale={locale}
          disabled={page <= 1}
          href={`${slug}${currentHash}`}
          className={cn(
            "min-h-8 min-w-6 shrink-0 hover:underline focus-visible:ring-1 focus-visible:ring-black disabled:pointer-events-none lg:min-w-8",
            "aria-disabled:text-grey-150 aria-disabled:opacity-100",
          )}
        >
          <ArrowStartIcon className="h-5 w-5" />
        </ButtonComponent>
        <ButtonComponent
          as={Link}
          locale={locale}
          disabled={page <= 1}
          href={backButtonLink}
          className={cn(
            "min-h-8 min-w-6 hover:underline focus-visible:ring-1 focus-visible:ring-black disabled:pointer-events-none lg:min-w-8",
            "flex items-center justify-center text-base font-normal uppercase",
            "aria-disabled:text-grey-150 aria-disabled:opacity-100",
          )}
        >
          <ArrowLeftIcon className="h-5 w-5" />
          {dictionary.previous}
        </ButtonComponent>

        <ul className="font-base flex h-full grow items-center justify-center overflow-x-auto uppercase text-grey-300">
          {page > pageRange && (
            <>
              <li>
                <ButtonComponent
                  as={Link}
                  locale={locale}
                  href={`${slug}${currentHash}`}
                  className="font-base ui-active:bg-transparent ui-active:text-black h-8 hover:underline"
                  aria-label="Goto page 1"
                >
                  1
                </ButtonComponent>
              </li>
              {page > pageRange + 1 && (
                <li
                  aria-hidden
                  className={cn(
                    "h-8 w-8 text-center leading-[2rem]",
                    classNames?.ellipsis,
                  )}
                >
                  ...
                </li>
              )}
            </>
          )}

          {pages.map((currentPage) => (
            <li key={currentPage}>
              <ButtonComponent
                as={Link}
                locale={locale}
                href={
                  currentPage > 1
                    ? `${slug}/${currentPage}${currentHash}`
                    : `${slug}${currentHash}`
                }
                disabled={page === currentPage}
                aria-current={page === currentPage}
                data-headlessui-state={page === currentPage ? "active" : ""}
                aria-label={`Goto page ${currentPage}`}
                className={cn(
                  "aria-disabled:text-black aria-disabled:opacity-100",
                  "min-h-8 min-w-6 hover:underline focus-visible:ring-1 focus-visible:ring-black disabled:pointer-events-none lg:min-w-8",
                )}
              >
                {currentPage}
              </ButtonComponent>
            </li>
          ))}

          {page < totalPages - pageRange - 1 && (
            <>
              <li
                aria-hidden
                className={cn(
                  "h-8 w-8 text-center leading-[2rem]",
                  classNames?.ellipsis,
                )}
              >
                ...
              </li>
              <li>
                <ButtonComponent
                  as={Link}
                  locale={locale}
                  href={`${slug}/${totalPages}${currentHash}`}
                  className={cn([
                    "h-8 hover:underline",
                    classNames?.pageButton,
                  ])}
                  aria-label={`Goto page ${totalPages}`}
                >
                  {totalPages}
                </ButtonComponent>
              </li>
            </>
          )}
        </ul>
        <ButtonComponent
          as={Link}
          locale={locale}
          disabled={page === totalPages}
          href={`${slug}/${Math.min(page + 1, totalPages)}${currentHash}`}
          className={cn(
            "min-h-8 min-w-6 hover:underline focus-visible:ring-1 focus-visible:ring-black disabled:pointer-events-none lg:min-w-8",
            "flex items-center justify-center text-base font-normal uppercase",
            "aria-disabled:text-grey-150 aria-disabled:opacity-100",
          )}
        >
          {dictionary.next}
          <ArrowRightIcon className="h-5 w-5" />
        </ButtonComponent>
        <ButtonComponent
          as={Link}
          locale={locale}
          disabled={page === totalPages}
          href={`${slug}/${totalPages}${currentHash}`}
          className={cn(
            "min-h-8 min-w-6 shrink-0 hover:underline focus-visible:ring-1 focus-visible:ring-black disabled:pointer-events-none lg:min-w-8",
            "aria-disabled:text-grey-150 aria-disabled:opacity-100",
          )}
        >
          <ArrowEndIcon className="h-5 w-5" />
        </ButtonComponent>
      </PaginationComponent>
    </nav>
  );
};

export default Pagination;
